import { z } from "zod";
import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";

const lineOverallStatsSchema = z.object({
    overallDefenseAVG: z.number(),
    overallOffensiveAVG: z.number(),
    overallTeamPlayAVG: z.number(),
    overallEffortAVG: z.number(),
    overallBonusAVG: z.number(),
    overallTotalAVG: z.number(),
});

const lineAVGSchema = z.object({
    lineName: z.string(),
    defenseAVG: z.number(),
    offensiveAVG: z.number(),
    teamPlayAVG: z.number(),
    effortAVG: z.number(),
    bonusAVG: z.number(),
    totalAVG: z.number(),
});

const matchAVGSchema = z.object({
    matchId: z.number(),
    matchDate: z.string(),
    enemyTeam: z.string(),
    lineAVG: z.array(lineAVGSchema),
    result: z.string(),
    chosenTeamScore: z.number(),
    opponentScore: z.number(),
});

export type LineOverallStats = z.infer<typeof lineOverallStatsSchema>;
export type MatchAVG = z.infer<typeof matchAVGSchema>;

const linescoresCompareAverageSchema = z.object({
    line1: lineOverallStatsSchema,
    line2: lineOverallStatsSchema,
    line3: lineOverallStatsSchema,
    line4: lineOverallStatsSchema,
});

const playerLongResponseSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    line: z.number().nullable().transform((v) => v ?? 0),
    role: z.string().default("Unknown"),
    timeOnIce: z.number().nullable(),
    pointsDefensive: z.number().nullable().transform((v) => v ?? 0),
    pointsOffensive: z.number().nullable().transform((v) => v ?? 0),
    pointsTeamPlay: z.number().nullable().transform((v) => v ?? 0),
    pointsEffort: z.number().nullable().transform((v) => v ?? 0),
    pointsBonus: z.number().nullable().transform((v) => v ?? 0),
    totalPoints: z.number().nullable().transform((v) => v ?? 0),
    matchesPlayed: z.number(),
});

export type PlayerLongResponse = z.infer<typeof playerLongResponseSchema>;

const linescoresCompareTeamSchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    average: linescoresCompareAverageSchema,
    playersByAverage: z.array(playerLongResponseSchema).default([]),
    playersByTotal: z.array(playerLongResponseSchema).default([]),
    matches: z.array(matchAVGSchema),
});

const linescoresCompareSchema = z.object({
    tournamentId: z.number(),
    chosenTeamData: linescoresCompareTeamSchema,
    team2: linescoresCompareTeamSchema,
});

export type LinescoresCompareTeam = z.infer<typeof linescoresCompareTeamSchema>;
export type LinescoresCompare = z.infer<typeof linescoresCompareSchema>;

const playerLinePeriodKpiSchema = z.object({
    playerId: z.number().nullable().optional(),
    playerName: z.string().nullable().optional(),
    playerLastName: z.string().nullable().optional(),
    matchId: z.number().nullable().optional(),
    line: z.number().default(0),
    role: z.string().default("Unknown"),
    pointsDefensive: z.number().nullable(),
    pointsOffensive: z.number().nullable(),
    pointsTeamPlay: z.number().nullable(),
    pointsEffort: z.number().nullable(),
    pointsBonus: z.number().nullable(),
    totalPoints: z.number().nullable(),
});

const linesKpiResponseSchema = z.object({
    line1: lineOverallStatsSchema,
    line2: lineOverallStatsSchema,
    line3: lineOverallStatsSchema,
    line4: lineOverallStatsSchema,
});

const teamLinescoreKpiSchema = z.object({
    teamId: z.number(),
    playersPeriodKpi: z.array(playerLinePeriodKpiSchema).default([]),
    lineAvgs: linesKpiResponseSchema,
});

const linePeriodDataSchema = z.object({
    periodId: z.number(),
    chosenTeamAverage: teamLinescoreKpiSchema,
    opponentTeamAverage: teamLinescoreKpiSchema.nullable().optional(),
});

const linescorePeriodResponseSchema = z.object({
    matchId: z.array(z.number()),
    linePeriodData: z.array(linePeriodDataSchema),
    overallLineData: linePeriodDataSchema.nullable().optional(),
});

export type PlayerLinePeriodKpi = z.infer<typeof playerLinePeriodKpiSchema>;
export type LinesKpiResponse = z.infer<typeof linesKpiResponseSchema>;
export type TeamLinescoreKpi = z.infer<typeof teamLinescoreKpiSchema>;
export type LinePeriodData = z.infer<typeof linePeriodDataSchema>;
export type LinescorePeriodResponse = z.infer<typeof linescorePeriodResponseSchema>;

export const fetchPeriodLinescores = async (matchIds: number[], teamId?: number): Promise<LinescorePeriodResponse> => {
    const response = await axiosInstance.post("/matches/linescores/periods", { matchIds, teamId });
    return linescorePeriodResponseSchema.parse(response.data);
};

export const periodLinescoresQueryOptions = (matchIds: number[], teamId?: number) =>
    queryOptions({
        queryKey: ["period-linescores", matchIds, teamId],
        queryFn: () => fetchPeriodLinescores(matchIds, teamId),
        staleTime: 5 * 60 * 1000,
    });

export interface CompareLinesRequest {
    coreTeamId: number;
    opponentTeamId: number;
    chosenMatchesIds: number[];
    team2MatchesIds: number[];
}

export const fetchLinescoresCompare = async (
    tournamentId: number,
    request: CompareLinesRequest = { coreTeamId: 0, opponentTeamId: 0, chosenMatchesIds: [], team2MatchesIds: [] }
): Promise<LinescoresCompare> => {
    const response = await axiosInstance.post(`/${tournamentId}/linescores/compare`, request);
    return linescoresCompareSchema.parse(response.data);
};

export const linescoresCompareQueryOptions = (
    tournamentId: number,
    request: CompareLinesRequest = { coreTeamId: 0, opponentTeamId: 0, chosenMatchesIds: [], team2MatchesIds: [] }
) =>
    queryOptions({
        queryKey: ["linescores-compare", tournamentId, request.coreTeamId, request.opponentTeamId, request.chosenMatchesIds, request.team2MatchesIds],
        queryFn: () => fetchLinescoresCompare(tournamentId, request),
        staleTime: 5 * 60 * 1000,
    });
